import { Box, Typography } from "@mui/material";
import { FC } from "react";
import { getWeightData, HeadData } from "../Data";
import { ControlledForm, CommonStyle } from "Components";
import { GlobalThresholdProps } from "DataTypes/SettingsFormatter.type";
import { CheckStringEmpty } from "Utils/common";
import {
  probUOMType,
  ThresholdType,
} from "DataTypes/Services/Settings.type";

const orderedRangeFields = HeadData.flatMap((headEl) =>
  headEl.range.map((unitEl) => ({
    alertType: headEl.id,
    fieldId: unitEl.id,
  }))
);

const getNeighborValue = (
  filterArr: ThresholdType[],
  index: number,
  offset: -1 | 1
): string => {
  const neighbor = orderedRangeFields[index + offset];
  if (!neighbor) return "";
  const item = filterArr.find((v) => v.alertType === neighbor.alertType);
  return item ? CheckStringEmpty(item[neighbor.fieldId]) : "";
};

const isWithinNeighbors = (
  value: string,
  prevVal: string,
  nextVal: string
): boolean => {
  if (value === "") return true;
  const num = Number(value);
  if (Number.isNaN(num) || !(num > 0)) return false;
  if (prevVal !== "" && !(num > Number(prevVal))) return false;
  if (nextVal !== "" && !(num < Number(nextVal))) return false;
  return true;
};

const isGreaterThanZero = (value: string): boolean => {
  if (value === "") return true;
  const num = Number(value);
  return !Number.isNaN(num) && num > 0;
};

const ProblemAlert: FC<GlobalThresholdProps> = ({
  data,
  deviceName,
  handleChange,
  singleDevice,
  isView,
  oldData,
}) => {
  const uniqueUomIds: probUOMType[] = [];
  data &&
    data.forEach((item) => {
      if (
        !uniqueUomIds.some(
          (uom) => uom.uomId !== "WEIGHT" && uom.uomId === item.uomId
        )
      ) {
        uniqueUomIds.push({
          uomId: item.uomId,
          uomLabel: item.uomLabel,
          problemId: item.problemId,
          problemName: item.problemName,
        });
      }
    });

  return (
    <Box display="flex">
      {!singleDevice && (
        <Typography
          width="14%"
          fontWeight="bold"
          sx={{
            ...CommonStyle.sxRowCenter,
            ...sxHeadBox,
          }}
        >
          {deviceName}
        </Typography>
      )}
      <Box width={singleDevice ? "100%" : "86%"}>
        {data &&
          uniqueUomIds
            .filter((filEl) =>
              data.find((findEl) => findEl.uomId == filEl.uomId)
            )
            .map((el) => {
              const filterArr = data.filter(
                (filEl1) => filEl1.uomId == el.uomId
              );
              const filterSingleData = filterArr.find(
                (item) => item.problemId === el.problemId
              );
              return (
                <Box display="flex" key={el.uomId}>
                  <Typography
                    width="15%"
                    sx={{
                      ...CommonStyle.sxRowCenter,
                      ...sxHeadBox,
                    }}
                    variant="body2"
                  >
                    {el.uomId === "WEIGHT" ? el.problemName : el.uomLabel}
                  </Typography>
                  {el.uomId !== "WEIGHT"
                    ? filterArr.length &&
                    HeadData.map((headEl) => {
                      return (
                        <Box
                          display="flex"
                          key={headEl.id}
                          mx={0.5}
                          width="17%"
                        >
                          {headEl.range.map((unitEl) => {
                            const valueEl = filterArr.find(
                              (valEl) => valEl.alertType == headEl.id
                            );
                            const originalEl = oldData?.find(
                              (valEl) =>
                                valEl.uomId == el.uomId &&
                                valEl.alertType == headEl.id
                            );
                            const isChanged =
                              !!valueEl &&
                              !!originalEl &&
                              CheckStringEmpty(valueEl[unitEl.id]) !==
                                CheckStringEmpty(originalEl[unitEl.id]);
                            const flatIndex = orderedRangeFields.findIndex(
                              (field) =>
                                field.alertType === headEl.id &&
                                field.fieldId === unitEl.id
                            );
                            const prevVal = getNeighborValue(
                              filterArr,
                              flatIndex,
                              -1
                            );
                            const nextVal = getNeighborValue(
                              filterArr,
                              flatIndex,
                              1
                            );
                            return (
                              <Box key={unitEl.id} width={unitEl.width}>
                                <ControlledForm.Input
                                  id={unitEl.id}
                                  value={valueEl ? valueEl[unitEl.id] : ""}
                                  sxProps={{
                                    ...sxInputBox,
                                    ...(isChanged && sxChangedInputBox),
                                  }}
                                  readOnly={!valueEl ? true : isView}
                                  textAlignCenter={true}
                                  type="number"
                                  minVal="0"
                                  {...(prevVal !== "" && {
                                    minVal: prevVal,
                                  })}
                                  {...(nextVal !== "" && {
                                    maxVal: nextVal,
                                  })}
                                  onChange={(inputVal) => {
                                    if (!valueEl) return;
                                    const newVal = CheckStringEmpty(inputVal);
                                    if (
                                      !isWithinNeighbors(
                                        newVal,
                                        prevVal,
                                        nextVal
                                      )
                                    ) {
                                      return;
                                    }
                                    handleChange({
                                      ...valueEl,
                                      [unitEl.id]: newVal,
                                    });
                                  }}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })
                    : filterSingleData &&
                    filterArr.length && (
                      <Box display="flex" mx={0.5} width="86%">
                        {getWeightData(filterSingleData)?.map((weightEl) => {
                          const originalWeightEl = oldData?.find(
                            (item) =>
                              item.problemId === filterSingleData.problemId
                          );
                          const isChanged =
                            weightEl.type === "input" &&
                            !!originalWeightEl &&
                            CheckStringEmpty(
                              filterSingleData[
                                weightEl.id as keyof ThresholdType
                              ]
                            ) !==
                              CheckStringEmpty(
                                originalWeightEl[
                                  weightEl.id as keyof ThresholdType
                                ]
                              );
                          return (
                            <>
                              {weightEl.type !== "input" ? (
                                <Box key={weightEl.id} width={weightEl.width}>
                                  <Typography
                                    textAlign="center"
                                    sx={{
                                      border: "1px solid",
                                      borderColor: "custom.main",
                                      color: weightEl?.color,
                                      fontSize:
                                        (weightEl?.id !== "criteria" &&
                                          "small") ||
                                        "",
                                      fontWeight:
                                        weightEl?.id === "criteria"
                                          ? "bold"
                                          : "",
                                    }}
                                    py={1}
                                  >
                                    {weightEl.label}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box key={weightEl.id} width={weightEl.width}>
                                  <ControlledForm.Input
                                    id={weightEl.id}
                                    value={
                                      weightEl.label
                                        ? String(weightEl.label)
                                        : ""
                                    }
                                    sxProps={{
                                      ...sxInputBox,
                                      ...(isChanged && sxChangedInputBox),
                                    }}
                                    readOnly={isView}
                                    textAlignCenter={true}
                                    type="number"
                                    minVal="0"
                                    onChange={(el) => {
                                      const newVal = CheckStringEmpty(el);
                                      if (!isGreaterThanZero(newVal)) return;
                                      handleChange({
                                        ...filterSingleData,
                                        [weightEl.id]: newVal,
                                      });
                                    }}
                                  />
                                </Box>
                              )}
                            </>
                          );
                        })}
                      </Box>
                    )}
                </Box>
              );
            })}
      </Box>
    </Box>
  );
};
export default ProblemAlert;

const sxHeadBox = {
  backgroundColor: "bg.main",
  border: "1px solid",
  borderColor: "custom.main",
  textAlign: "center",
};

const sxInputBox = {
  boxShadow: 0,
  borderRadius: 0,
  border: "1px solid",
  borderColor: "custom.main",
};

const sxChangedInputBox = {
  borderColor: "warning.main",
  backgroundColor: "warning.light",
};
